import { FC } from "react";

import {
  DonateButtonGuideUrl,
  EmbedFormGuideUrl,
  FormCategory,
  FormQueryParams,
  FormType,
  getDonationOrTicketingEmbedLink,
  getFormEmbedCode,
  getLeaderboardEmbedCode,
  getThermometerEmbedCode,
  LeaderboardEmbedGuideUrl,
  PopupEmbedGuideUrl,
  ThermometerEmbedGuideUrl,
} from "@simplyk/common";

import { SimpleEmbedInstructionsTab, SimpleEmbedInstructionsTabProps } from "./SimpleEmbedInstructionsTab";

import { EMBEDDED_BUTTON_LINK_ATTR } from "@/common/constants/EmbedForm";
import { FRONTEND_URL } from "@/common/routes/routes";
import {
  ButtonPopupInstructionsTabProps,
  ButtonPopupInstructionsTab,
} from "@/components/ShareFormModal/ButtonPopupInstructionsTab";
import {
  LinkedButtonInstructionsTabProps,
  LinkedButtonInstructionsTab,
} from "@/components/ShareFormModal/LinkedButtonInstructionsTab";

type FormEmbedInstructionsTabProps = Omit<SimpleEmbedInstructionsTabProps, "embedCode" | "guideLinks" | "embedType"> & {
  formId: string;
  formPath: string;
  formType: FormType;
  formCategory: FormCategory;
  isPrimaryCampaign?: boolean;
  isPrimaryTeam?: boolean;
};

export const FormEmbedInstructionsTab: FC<FormEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  formType,
  formCategory,
  isPrimaryCampaign,
  isPrimaryTeam,
  ...rest
}) => {
  const embedPath = getDonationOrTicketingEmbedLink({
    form: {
      id: formId,
      path: formPath,
      formType,
      formCategory: FormCategory[formCategory],
      isPrimaryCampaign,
      isPrimaryTeam,
    },
    isoLocale: null,
  });

  const embedCode = getFormEmbedCode({ frontendUrl: FRONTEND_URL, embedPath, formType });

  return (
    <SimpleEmbedInstructionsTab
      {...rest}
      embedType="Form"
      embedCode={embedCode}
      guideLinks={EmbedFormGuideUrl}
      formCategory={formCategory}
    />
  );
};

type ThermometerEmbedInstructionsTabProps = Omit<
  SimpleEmbedInstructionsTabProps,
  "embedCode" | "guideLinks" | "embedType"
> & {
  formId: string;
  formPath: string;
};

export const ThermometerEmbedInstructionsTab: FC<ThermometerEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  ...rest
}) => {
  const embedCode = getThermometerEmbedCode({ frontendUrl: FRONTEND_URL, path: formPath || formId });

  return (
    <SimpleEmbedInstructionsTab
      {...rest}
      embedType="Thermometer"
      embedCode={embedCode}
      guideLinks={ThermometerEmbedGuideUrl}
      formCategory={rest.formCategory}
    />
  );
};

type LeaderboardEmbedInstructionsTabProps = Omit<
  SimpleEmbedInstructionsTabProps,
  "embedCode" | "guideLinks" | "embedType"
> & {
  formId: string;
  formPath: string;
};

export const LeaderboardEmbedInstructionsTab: FC<LeaderboardEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  ...rest
}) => {
  const embedCode = getLeaderboardEmbedCode({ frontendUrl: FRONTEND_URL, path: formPath || formId });

  return (
    <SimpleEmbedInstructionsTab
      {...rest}
      embedType="Leaderboard"
      embedCode={embedCode}
      guideLinks={LeaderboardEmbedGuideUrl}
      formCategory={rest.formCategory}
    />
  );
};

type ButtonPopupEmbedInstructionsTabProps = Omit<
  ButtonPopupInstructionsTabProps,
  "embedCode" | "guideLinks" | "embedType"
> & {
  formId: string;
  formPath: string;
  formType: FormType;
  formCategory: FormCategory;
  isPrimaryCampaign: boolean;
  isPrimaryTeam: boolean;
};

export const ButtonPopupEmbedInstructionsTab: FC<ButtonPopupEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  formType,
  formCategory,
  isPrimaryCampaign,
  isPrimaryTeam,
  ...rest
}) => {
  const embedPath = getDonationOrTicketingEmbedLink({
    form: {
      id: formId,
      path: formPath,
      formType,
      formCategory: FormCategory[formCategory],
      isPrimaryCampaign,
      isPrimaryTeam,
    },
    isoLocale: null,
  });

  const embedLink = `${FRONTEND_URL}${embedPath}`;

  const embedCode = `${EMBEDDED_BUTTON_LINK_ATTR}="${embedLink}?${FormQueryParams.Modal}=true"`;

  return (
    <ButtonPopupInstructionsTab
      {...rest}
      guideLinks={PopupEmbedGuideUrl}
      embedCode={embedCode}
      formCategory={formCategory}
    />
  );
};

type LinkedButtonEmbedInstructionsTabProps = Omit<LinkedButtonInstructionsTabProps, "formLink" | "guideLinks"> & {
  formLink: string;
};

export const LinkedButtonEmbedInstructionsTab: FC<LinkedButtonEmbedInstructionsTabProps> = ({ formLink, ...rest }) => {
  return <LinkedButtonInstructionsTab {...rest} guideLinks={DonateButtonGuideUrl} formLink={formLink} />;
};
